import { pageQueryParams } from '@/components/search/pageQueryParams'
import { SortableHeader } from '@/components/search/SortableHeader'
import './groups.css'
import React, { useRef, useState, useEffect } from 'react'
import { Services } from '@/services/services'
import { useForm } from 'react-hook-form'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { ErrorBoxComponent } from '@/components/forms/common/FormBase'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { BackButton } from '@/components/layout/NavBar'
import { useModal } from '@/components/modal/Modal'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { CurrentSession } from '@/services/repository/session'
import { SimpleUser } from '../users/Users'
import { formatDate } from '@/common/time'
import { groupLabel } from '@/components/usergroup/group'
import { namespaceFormErrors, useNamespaces } from '@/pages/communal/namespaces/common'
import { isAdminRole } from '@/services/repository/users'

export interface SimpleUserGroup {
  checklistEnabled?: boolean
  id: string
  name: string
  namespaceId?: string
  namespaceName?: string
  canOpen?: boolean
  canEdit?: boolean
  canDelete?: boolean
  canMove?: boolean
}

export interface UserGroupFormData {
  checklistEnabled: boolean
  id?: string
  name: string
  namespaceId: string
  _general?: string
}

export interface SuperAdminEditUserGroupPopupProps {
  services: Services
  session: CurrentSession
  onClose: () => void
  userGroupId: string | undefined
}
export interface SuperAdminInsertUserGroupPopupProps {
  services: Services
  session: CurrentSession
  onClose: () => void
  onInsert?: (data: {
    id: string
    name: string
  }) => void
  defaultValues?: Partial<UserGroupFormData>
}

// Both legacy popups use the same namespace-aware form, including wizard creation.
function UserGroupForm({ services, session, onClose, onInsert, defaultValues, userGroupId }: SuperAdminInsertUserGroupPopupProps & { userGroupId?: string }) {
  const form = useForm<UserGroupFormData>({ defaultValues })
  const { namespaces, error } = useNamespaces(services)
  const [group, setGroup] = useState<SimpleUserGroup>()
  const [loadError, setLoadError] = useState(false)
  const admin = isAdminRole(session.user.role)

  useEffect(() => {
    if (!userGroupId) return
    let active = true
    services.session.fetchWithToken(`/api/communal/groups/byid/${userGroupId}`).then(async r => {
      if (!r.ok) throw new Error('Group unavailable')
      const data = await r.json()
      if (active) { setGroup(data); form.reset(data) }
    }).catch(() => { if (active) setLoadError(true) })
    return () => { active = false }
  }, [userGroupId, services])

  useEffect(() => {
    if (!userGroupId && namespaces?.length === 1 && !form.getValues('namespaceId')) form.setValue('namespaceId', namespaces[0].id)
  }, [namespaces, userGroupId])

  const submit = async (data: UserGroupFormData) => {
    form.clearErrors()
    try {
      const response = await services.session.fetchWithToken(userGroupId ? `/api/communal/groups/byid/${userGroupId}/update` : '/api/communal/groups', {
        method: userGroupId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: data.name.trim(), namespaceId: data.namespaceId,
          ...(userGroupId && admin ? { checklistEnabled: !!data.checklistEnabled } : {}) })
      })
      if (!response.ok) { await namespaceFormErrors(form, session, response); return }
      const result = await response.json()
      onInsert?.(result)
      onClose()
    } catch { form.setError('_general', { message: session.translates.for('errors.namespace_request_failed') }) }
  }

  if (error || loadError) return <p className='errorMessage'>{session.translates.for('errors.namespace_request_failed')}</p>
  if (!namespaces || (userGroupId && !group)) return <div className='spinner' />
  const choices = group?.namespaceId && !namespaces.some(n => n.id === group.namespaceId)
    ? [...namespaces, { id: group.namespaceId, name: group.namespaceName ?? '' }] : namespaces

  return <form onSubmit={form.handleSubmit(submit)} className='main-form' autoComplete='off'>
    <div className='form-field'>
      <label htmlFor='group-namespace'>{session.translates.for('namespace.label')}</label>
      {userGroupId && !admin ? <>
        <input type='hidden' {...form.register('namespaceId')} />
        <span>{group?.namespaceName}</span>
      </> : <select id='group-namespace' {...form.register('namespaceId', { required: session.translates.for('errors.required') })}>
        <option value=''>{session.translates.for('namespace.select')}</option>
        {choices.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
      </select>}
      {ErrorBoxComponent(form, session.translates, 'namespaceId')}
    </div>
    <div className='form-field'>
      <label htmlFor='group-name'>{session.translates.for('elements.table.groups.name')}</label>
      <input id='group-name' type='text' {...form.register('name', { required: session.translates.for('errors.required'), maxLength: { value: 250, message: session.translates.for('errors.namespace_name_invalid') }, validate: value => !!value.trim() || session.translates.for('errors.required') })} />
      {ErrorBoxComponent(form, session.translates, 'name')}
    </div>
    {userGroupId && admin && <div className='form-field'>
      <label className='user-group-checklist-toggle' htmlFor='group-checklist'>
        <input id='group-checklist' type='checkbox' {...form.register('checklistEnabled')} />
        <span>{session.translates.for('forms.groups.checklist_enabled')}</span>
      </label>
    </div>}
    {ErrorBoxComponent(form, session.translates, '_general')}
    {!userGroupId && namespaces.length === 0 && <p>{session.translates.for('namespace.no_assigned')}</p>}
    <button className='submit-button' type='submit' disabled={form.formState.isSubmitting || (!userGroupId && namespaces.length === 0)}>
      {session.translates.for('forms.groups.submit')}
    </button>
  </form>
}

export const SuperAdminEditUserGroupPopup = (props: SuperAdminEditUserGroupPopupProps) => <UserGroupForm {...props} />
export const SuperAdminInsertUserGroupPopup = (props: SuperAdminInsertUserGroupPopupProps) => <UserGroupForm {...props} />

// -------- USER RELATION POPUP --------

const RelatedUsersPage = ({
  services,
  session,
  groupId,
  onClose
}: {
  services: Services,
  session: CurrentSession,
  groupId: string | undefined,
  onClose: () => void
}) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const [relationError, setRelationError] = useState<string>()

  const tableParams: SearchTableParams<SimpleUser> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUser>> => {
      return services.session.fetchWithToken(`/api/communal/groups/byid/${groupId}/users/search?${pageQueryParams(q)}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <></>,
    defaultSortBy: 'createdAt',
    header: (query, onSort) => <>
      <div className='hcol'></div>
      <SortableHeader field='createdAt' query={query} onSort={onSort} className='hcol desktop-only'>{session.translates.for('elements.table.users.createdAt')}</SortableHeader>
      <SortableHeader field='email' query={query} onSort={onSort} className='hcol desktop-only'>{session.translates.for('elements.table.users.email')}</SortableHeader>
      <SortableHeader field='username' query={query} onSort={onSort}>{session.translates.for('elements.table.users.username')}</SortableHeader>
    </>,
    row: (row, state, setState) => <>
      <div className='col icocol' onClick={() => {
        setState((state) => {
          if (state === undefined) return;
          state.data = state.data?.map(item =>
            item.id === row.id ? { ...item, isRelated: undefined } : item
          )
          return { ...state }
        })

        setRelationError(undefined)
        services.session.fetchWithToken(`/api/communal/groups/byid/${groupId}/relations/users/${row.id}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            related: !row.isRelated
          }),
        }).then(async (resp) => {
          if (!resp.ok) {
            const data = await resp.json().catch(() => ({}))
            setRelationError(session.translates.for(data.errors?._general?.message ?? 'errors.namespace_request_failed'))
          }
          setState((state) => {
            if (state === undefined) return;
            state.data = state.data?.map(item =>
              item.id === row.id ? { ...item, isRelated: resp.ok ? !row.isRelated : row.isRelated } : item
            )
            return { ...state }
          })
        }).catch(() => {
          setRelationError(session.translates.for('errors.namespace_request_failed'))
          setState(previous => previous ? { ...previous, data: previous.data?.map(item => item.id === row.id ? { ...item, isRelated: row.isRelated } : item) } : previous)
        })

      }}>
        {row.isRelated === undefined ? <div className='spinner'></div> : row.isRelated ? <i className='svgfont-checkbox' /> : <i className='svgfont-checkbox-blank' />}
      </div>
      <div className='col desktop-only'>{formatDate(new Date(row.createdAt))}</div>
      <div className='col desktop-only'>{row.email}</div>
      <div className='col'>{row.username}</div>
    </>,
    translates: session.translates
  }

  return <>{relationError && <p className='errorMessage'>{relationError}</p>}<SearchTable ref={searchTableRef} {...tableParams} /></>
}

// export default RelatedUsersPage;

// ------- MACHINE RELATION POPUP ------

export interface SimpleMachine {
  id: string
  name: string
  manufacturer: string
  model: string
  internalNumber: string
  isRelated?: boolean
}

const RelatedMachinesPage = ({
  services,
  session,
  groupId,
  onClose
}: {
  services: Services,
  session: CurrentSession,
  groupId: string | undefined,
  onClose: () => void
}) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const [relationError, setRelationError] = useState<string>()

  const tableParams: SearchTableParams<SimpleMachine> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleMachine>> => {
      return services.session.fetchWithToken(
        `/api/communal/groups/byid/${groupId}/machines/search?${pageQueryParams(q)}`
      ).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <></>,
    defaultSortBy: 'manufacturer',
    header: (query, onSort) => <>
      <div className='hcol'></div>
      <SortableHeader field='manufacturer' query={query} onSort={onSort}>{session.translates.for('elements.table.machines.manufacturer')}</SortableHeader>
      <SortableHeader field='model' query={query} onSort={onSort}>{session.translates.for('elements.table.machines.model')}</SortableHeader>
      <SortableHeader field='number' query={query} onSort={onSort}>{session.translates.for('elements.table.machines.number')}</SortableHeader>
      {/* <div className='hcol'>{session.translates.for('elements.table.machines.name')}</div> */}
    </>,
    row: (row, state, setState) => <>
      <div className='col icocol' onClick={() => {
        setState((state) => {
          if (state === undefined) return
          state.data = state.data?.map(item =>
            item.id === row.id ? { ...item, isRelated: undefined } : item
          )
          return { ...state }
        })

        setRelationError(undefined)
        services.session.fetchWithToken(`/api/communal/groups/byid/${groupId}/relations/machines/${row.id}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            related: !row.isRelated
          }),
        }).then(async (resp) => {
          if (!resp.ok) {
            const data = await resp.json().catch(() => ({}))
            setRelationError(session.translates.for(data.errors?._general?.message ?? 'errors.namespace_request_failed'))
          }
          setState((state) => {
            if (state === undefined) return
            state.data = state.data?.map(item =>
              item.id === row.id ? { ...item, isRelated: resp.ok ? !row.isRelated : row.isRelated } : item
            )
            return { ...state }
          })
        }).catch(() => {
          setRelationError(session.translates.for('errors.namespace_request_failed'))
          setState(previous => previous ? { ...previous, data: previous.data?.map(item => item.id === row.id ? { ...item, isRelated: row.isRelated } : item) } : previous)
        })

      }}>
        {row.isRelated === undefined ? <div className='spinner'></div> : row.isRelated ? <i className='svgfont-checkbox' /> : <i className='svgfont-checkbox-blank' />}
      </div>
      <div className='col'>{row.manufacturer}</div>
      <div className='col'>{row.model}</div>
      <div className='col'>{row.internalNumber || row.id}</div>
      {/* <div className='col'>{row.name}</div> */}
    </>,
    translates: session.translates
  }

  return <>{relationError && <p className='errorMessage'>{relationError}</p>}<SearchTable ref={searchTableRef} {...tableParams} /></>
}

// ------- REMOVE USER POPUP -----------

type SuperAdminRemoveUserGroupPopupProps = {
  services: Services,
  session: CurrentSession,
  onClose: () => void,
  group: SimpleUserGroup | undefined
}

export const SuperAdminRemoveUserGroupPopup = ({ services, session, onClose, group }: SuperAdminRemoveUserGroupPopupProps) => {
  const form = useForm()
  const [error, setError] = useState<boolean>(false)

  useEffect(() => {
    setError(false)
  }, [group?.id])

  if (group === undefined) {
    return null
  }

  const remove = (): Promise<void> => {
    return services.session.fetchWithToken(`/api/communal/groups/byid/${group.id}/delete`, {
      method: 'DELETE'
    }).then(async (r) => {
      if (r.status !== 200) {
        setError(true)
        return
      }
      setError(false)
      onClose()
    })
  }

  return (
    <div className='dialog'>
      <div className='dialog-text'>
        {session.translates.for('elements.table.usergroups.remove_group_ask').replace('{groupname}', group.name)}
      </div>
      <div className='dialog-btns'>
        <div className='dialog-btn' onClick={onClose}>
          <i className='svgfont-no' />
          {session.translates.for('elements.dialog.yesno.no')}
        </div>
        <div className='dialog-btn' onClick={remove}>
          <i className='svgfont-yes' />
          {session.translates.for('elements.dialog.yesno.yes')}
        </div>
        {error ? (
          <div className='dialog-error'>
            {session.translates.for('elements.table.usergroups.remove_group_error')}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function UserGroupMenuPopup({ services, session, onClose, group, onChanged }: {
  services: Services
  session: CurrentSession
  onClose: () => void
  group: SimpleUserGroup
  onChanged: () => void
}) {
  const { openModal } = useModal()
  const menu: Menu = {
    title: groupLabel(group),
    rows: [
      ...(group.canEdit ? [{
        icon: 'svgfont-edit',
        title: 'namespace.edit',
        action: () => openModal(props => <SuperAdminEditUserGroupPopup {...props} services={services} session={session} userGroupId={group.id} onClose={() => { props.onClose(); onChanged() }} />)
      }] : []),
      ...(group.canOpen ? [{
        icon: 'svgfont-users',
        title: 'namespace.users',
        action: () => openModal(props => <RelatedUsersPage {...props} groupId={group.id} services={services} session={session} />)
      }, {
        icon: 'svgfont-machine',
        title: 'namespace.machine',
        action: () => openModal(props => <RelatedMachinesPage {...props} groupId={group.id} services={services} session={session} />)
      }] : []),
      ...(group.canDelete ? [{
        icon: 'svgfont-remove',
        title: 'namespace.delete',
        action: () => openModal(props => <SuperAdminRemoveUserGroupPopup {...props} group={group} services={services} session={session} onClose={() => { props.onClose(); onChanged() }} />)
      }] : [])
    ]
  }
  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

// -------- USER GROUPS PAGE -----------
export const SuperAdminUserGroupsPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()
  const { namespaces } = useNamespaces(services)
  const [namespaceId, setNamespaceId] = useState('')
  const [groupNamespaces, setGroupNamespaces] = useState<{id: string, name: string}[]>([])
  useEffect(() => {
    services.session.fetchWithToken('/api/communal/usergroups/my').then(r => r.json()).then((groups: SimpleUserGroup[]) => {
      const choices = new Map<string, string>()
      for (const group of groups ?? []) if (group.namespaceId) choices.set(group.namespaceId, group.namespaceName ?? '')
      setGroupNamespaces(Array.from(choices, ([id, name]) => ({ id, name })))
    }).catch(() => {})
  }, [services])
  const filterNamespaces = [...(namespaces ?? []), ...groupNamespaces.filter(n => !namespaces?.some(a => a.id === n.id))]

  const tableParams: SearchTableParams<SimpleUserGroup> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUserGroup>> => {
      return services.session.fetchWithToken(`/api/communal/groups/search?namespaceId=${namespaceId}&${pageQueryParams(q)}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => namespaces?.length ? (
      <div className='tool' onClick={() => openModal((props) =>
        <SuperAdminInsertUserGroupPopup {...props} services={services} session={session} defaultValues={{ namespaceId }} onClose={() => { props.onClose(); searchTableRef.current?.refresh() }} />
      )}>
        <i className='svgfont-add' />
      </div>
    ) : <></>,
    defaultSortBy: 'name',
    header: (query, onSort) => (
      <>
        <SortableHeader field='name' query={query} onSort={onSort}>{session.translates.for('elements.table.groups.name')}</SortableHeader>
        <div className='hcol'></div>
      </>
    ),
    row: (row) => {
      const canManage = row.canEdit || row.canOpen || row.canDelete
      return (
      <>
        <div className='col'>{groupLabel(row)}</div>
        <div className='col icocol' role={canManage ? 'button' : undefined} tabIndex={canManage ? 0 : undefined} aria-label={canManage ? groupLabel(row) : undefined} onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              event.currentTarget.click()
            }
          }} onClick={canManage ? () => openModal(props => <UserGroupMenuPopup {...props} services={services} session={session} group={row} onChanged={() => searchTableRef.current?.refresh()} />) : undefined}>
          {canManage && <i className='svgfont-menu' />}
        </div>
      </>
      )
    },
    translates: session.translates
  }

  return (
    <LoggedInPageLayout pageName='communal-usergroups' services={services} session={session}
      navbarLeft={<BackButton services={services} translates={session.translates} />}>
      <div className='user-groups-namespace-filter'>
        <label htmlFor='group-namespace-filter'>
          <i className='svgfont-users' aria-hidden='true' />
          {session.translates.for('namespace.label')}
        </label>
        <div className='user-groups-namespace-select'>
          <select id='group-namespace-filter' value={namespaceId} onChange={e => setNamespaceId(e.target.value)}>
            <option value=''>{session.translates.for('namespace.all')}</option>
            {filterNamespaces.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
        </div>
      </div>
      <SearchTable key={namespaceId} ref={searchTableRef} {...tableParams} />
    </LoggedInPageLayout>
  )
}
